import { useLocation, useNavigate } from 'react-router';

import {
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
  isLocaleAwarePublicPath,
  localizePublicPath,
  persistLanguagePreference,
  pickLocalized,
  useLanguage,
  type SupportedLanguage,
} from '@/features/i18n';

interface LanguageSwitcherProps {
  ariaLabel?: string;
  navigationMode?: 'spa' | 'document';
  documentNavigate?: (href: string) => void;
}

export default function LanguageSwitcher({
  ariaLabel,
  navigationMode = 'spa',
  documentNavigate = (href) => window.location.assign(href),
}: LanguageSwitcherProps = {}) {
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const localizedAriaLabel = pickLocalized(language, {
    ko: '언어 설정',
    en: 'Language selection',
    ja: '言語設定',
  });
  const resolvedAriaLabel = ariaLabel ?? localizedAriaLabel;

  function handleLanguageChange(nextLanguage: string) {
    const resolvedLanguage = nextLanguage as SupportedLanguage;
    const nextPath = isLocaleAwarePublicPath(location.pathname)
      ? localizePublicPath(resolvedLanguage, location.pathname)
      : location.pathname;
    if (navigationMode === 'document') {
      persistLanguagePreference(resolvedLanguage);
      documentNavigate(`${nextPath}${location.search}${location.hash}`);
      return;
    }

    setLanguage(resolvedLanguage);
    if (!isLocaleAwarePublicPath(location.pathname)) {
      return;
    }

    navigate(
      {
        pathname: nextPath,
        search: location.search,
        hash: location.hash,
      },
      { replace: true },
    );
  }

  return (
    <div className="language-switcher">
      <select
        className="language-switcher-select"
        aria-label={resolvedAriaLabel}
        title={resolvedAriaLabel}
        data-navigation-mode={navigationMode}
        value={language}
        onChange={(event) => handleLanguageChange(event.target.value)}
      >
        {SUPPORTED_LANGUAGES.map((option) => (
          <option key={option} value={option}>
            {LANGUAGE_LABELS[option]}
          </option>
        ))}
      </select>
      <span className="language-switcher-caret" aria-hidden="true">
        ▾
      </span>
    </div>
  );
}
